import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Calendar, ClipboardList, PenLine, Award, Clock, Users, CheckCircle, School, Megaphone, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function TeacherDashboard() {
  const location = useLocation();
  const pathTab = location.pathname.split('/').pop();
  const tabMap = { classes: 'classes', attendance: 'attendance', assignments: 'assignments', gradebook: 'gradebook', timetable: 'timetable', announcements: 'announcements', messages: 'messages', comments: 'comments', news: 'news' };
  const [activeTab, setActiveTab] = useState(tabMap[pathTab] || 'dashboard');

  React.useEffect(() => {
    setActiveTab(tabMap[pathTab] || 'dashboard');
  }, [pathTab]);

  const [dashboard, setDashboard] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceEntries, setAttendanceEntries] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({ title: '', instructions: '', class_id: '', subject_id: '', due_date: '' });
  const [subjects, setSubjects] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [gradeForm, setGradeForm] = useState({ subject_id: '', class_id: '', assessment_name: '', term: 'Term 1', entries: [] });
  const [timetable, setTimetable] = useState([]);
  const [assignmentFiles, setAssignmentFiles] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [viewingClass, setViewingClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [termComments, setTermComments] = useState([]);
  const [commentTerm, setCommentTerm] = useState('Term 1');
  const [teacherMessages, setTeacherMessages] = useState([]);
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [replyForm, setReplyForm] = useState({ recipient_id: '', recipient_role: 'parent', subject: '', content: '' });

  useEffect(() => {
    api.get('/teacher/dashboard').then(r => setDashboard(r.data)).catch(() => {});
    api.get('/teacher/classes').then(r => setClasses(r.data)).catch(() => {});
    api.get('/teacher/timetable').then(r => setTimetable(r.data)).catch(() => {});
    api.get('/teacher/assignments').then(r => setAssignments(r.data)).catch(() => {});
    api.get('/teacher/subjects').then(r => setSubjects(r.data)).catch(() => {});
    api.get('/teacher/announcements').then(r => setAnnouncements(r.data)).catch(() => {});
    api.get('/teacher/messages').then(r => setTeacherMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedClass) {
      api.get(`/teacher/class/${selectedClass}/students`).then(r => {
        setStudents(r.data);
        setAttendanceEntries(r.data.map(s => ({ student_id: s.id, status: 'present', notes: '' })));
        setGradeForm(prev => ({ ...prev, class_id: selectedClass, subject_id: '', entries: r.data.map(s => ({ student_id: s.id, marks: 0, total_marks: 100, comments: '' })) }));
        setTermComments(r.data.map(s => ({ student_id: s.id, comment: '' })));
      }).catch(() => {});
      // Load subjects for this class (teacher-specific)
      api.get(`/teacher/subjects?class_id=${selectedClass}`).then(r => setClassSubjects(r.data)).catch(() => setClassSubjects([]));
      // Load existing comments
      api.get(`/teacher/term-comments?class_id=${selectedClass}`).then(r => {
        if (r.data.length > 0) {
          setTermComments(prev => prev.map(tc => {
            const existing = r.data.find(c => c.student_id === tc.student_id);
            return existing ? { ...tc, comment: existing.comment, term: existing.term } : tc;
          }));
        }
      }).catch(() => {});
    }
  }, [selectedClass]);

  const viewClassStudents = async (cls) => {
    setViewingClass(cls);
    try {
      const r = await api.get(`/teacher/class/${cls.id}/students`);
      setClassStudents(r.data);
    } catch { setClassStudents([]); }
  };

  const submitAttendance = async () => {
    if (!selectedClass) { toast.error('Select a class first'); return; }
    try {
      await api.post('/teacher/attendance', { class_id: selectedClass, date: attendanceDate, entries: attendanceEntries });
      toast.success('Attendance marked successfully');
    } catch { toast.error('Error marking attendance'); }
  };

  const submitAssignment = async () => {
    try {
      const payload = { ...assignmentForm, attachments: [] };
      if (assignmentFiles.length > 0) {
        for (const file of assignmentFiles) {
          const fd = new FormData();
          fd.append('file', file);
          try {
            const { data: fileData } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            payload.attachments.push({ storage_path: fileData.path, original_filename: file.name, content_type: file.type });
          } catch { /* skip failed uploads */ }
        }
      }
      await api.post('/teacher/assignments', payload);
      toast.success('Assignment created');
      setShowAssignmentDialog(false);
      setAssignmentFiles([]);
      setAssignmentForm({ title: '', instructions: '', class_id: '', subject_id: '', due_date: '' });
      api.get('/teacher/assignments').then(r => setAssignments(r.data));
    } catch { toast.error('Error creating assignment'); }
  };

  const submitGrades = async () => {
    if (!gradeForm.subject_id || !gradeForm.assessment_name) {
      toast.error('Please fill in subject and assessment name');
      return;
    }
    try {
      await api.post('/teacher/grades', gradeForm);
      toast.success('Grades saved');
    } catch { toast.error('Error saving grades'); }
  };

  const saveTermComments = async () => {
    if (!selectedClass) { toast.error('Select a class first'); return; }
    let saved = 0;
    for (const tc of termComments) {
      if (tc.comment.trim()) {
        try {
          await api.post('/teacher/term-comments', { student_id: tc.student_id, class_id: selectedClass, term: commentTerm, comment: tc.comment });
          saved++;
        } catch { /* continue */ }
      }
    }
    toast.success(`Saved comments for ${saved} student${saved !== 1 ? 's' : ''}`);
  };

  const replyToMessage = (msg) => {
    setReplyForm({
      recipient_id: msg.sender_id || '',
      recipient_role: msg.sender_role || 'parent',
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject || ''}`,
      content: '',
    });
    setShowReplyDialog(true);
  };

  const sendReply = async () => {
    if (!replyForm.content.trim()) { toast.error('Message cannot be empty'); return; }
    try {
      await api.post('/teacher/messages', replyForm);
      toast.success('Reply sent');
      setShowReplyDialog(false);
      api.get('/teacher/messages').then(r => setTeacherMessages(r.data));
    } catch { toast.error('Failed to send'); }
  };

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const groupedTT = DAYS.reduce((acc, d) => { acc[d] = timetable.filter(e => e.day === d).sort((a, b) => a.period - b.period); return acc; }, {});

  return (
    <div data-testid="teacher-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A]">Teacher Portal</h1>
        {dashboard?.teacher && (
          <p className="text-sm text-slate-500 mt-1">
            {dashboard.teacher.first_name} {dashboard.teacher.last_name} | {classes.length} class{classes.length !== 1 ? 'es' : ''} assigned
          </p>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard"><Clock className="w-3.5 h-3.5 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="classes"><School className="w-3.5 h-3.5 mr-1" /> My Classes</TabsTrigger>
          <TabsTrigger value="attendance"><ClipboardList className="w-3.5 h-3.5 mr-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="assignments"><PenLine className="w-3.5 h-3.5 mr-1" /> Assignments</TabsTrigger>
          <TabsTrigger value="gradebook"><Award className="w-3.5 h-3.5 mr-1" /> Gradebook</TabsTrigger>
          <TabsTrigger value="comments"><MessageSquare className="w-3.5 h-3.5 mr-1" /> Comments</TabsTrigger>
          <TabsTrigger value="timetable"><Calendar className="w-3.5 h-3.5 mr-1" /> Timetable</TabsTrigger>
          <TabsTrigger value="announcements"><Megaphone className="w-3.5 h-3.5 mr-1" /> News</TabsTrigger>
          <TabsTrigger value="messages"><MessageSquare className="w-3.5 h-3.5 mr-1" /> Messages</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="dashboard">
          {dashboard && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500">Today's Classes</p><p className="text-2xl font-black text-[#1B2A4A]">{dashboard.today_classes?.length || 0}</p></CardContent></Card>
                <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500">Pending Attendance</p><p className="text-2xl font-black text-amber-600">{dashboard.pending_attendance || 0}</p></CardContent></Card>
                <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500">Total Assignments</p><p className="text-2xl font-black text-emerald-600">{dashboard.total_assignments || 0}</p></CardContent></Card>
                <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500">My Classes</p><p className="text-2xl font-black text-sky-600">{classes.length}</p></CardContent></Card>
              </div>
              {dashboard.today_classes?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Today's Schedule</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dashboard.today_classes.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-semibold text-sm text-[#1B2A4A]">Period {c.period}: {c.subject_name}</p>
                            <p className="text-xs text-slate-500">{c.class_name} | {c.start_time} - {c.end_time}</p>
                          </div>
                          <Badge variant="outline">{c.room || 'TBA'}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {dashboard.today_classes?.length === 0 && (
                <p className="text-slate-400 text-center py-8">No classes scheduled for today</p>
              )}
            </>
          )}
        </TabsContent>

        {/* My Classes */}
        <TabsContent value="classes">
          {classes.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No classes assigned yet. Ask your admin to assign classes to you.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {classes.map(cls => (
                <Card key={cls.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewClassStudents(cls)} data-testid={`class-card-${cls.id}`}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-[#1B2A4A]">{cls.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{cls.division} | {cls.grade}</p>
                      </div>
                      <Badge className="bg-[#1B2A4A] text-white text-xs">
                        <Users className="w-3 h-3 mr-1" />{cls.student_count || 0}
                      </Badge>
                    </div>
                    {cls.room && <p className="text-xs text-slate-400 mt-2">Room: {cls.room}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Class Students Dialog */}
          <Dialog open={!!viewingClass} onOpenChange={() => setViewingClass(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{viewingClass?.name} - Student Roster</DialogTitle>
              </DialogHeader>
              {classStudents.length === 0 ? (
                <p className="text-slate-400 text-sm py-4">No students enrolled in this class</p>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>#</TableHead><TableHead>Name</TableHead><TableHead>Adm No.</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {classStudents.map((s, i) => (
                        <TableRow key={s.id}>
                          <TableCell className="text-slate-400 text-sm">{i + 1}</TableCell>
                          <TableCell className="font-semibold text-sm">{s.first_name} {s.last_name}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs font-mono">{s.admission_number}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingClass(null)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-60" data-testid="attendance-class-select"><SelectValue placeholder="Select Class" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} className="w-48" data-testid="attendance-date-input" />
          </div>
          {!selectedClass && <p className="text-slate-400 text-center py-8">Select a class to mark attendance</p>}
          {selectedClass && students.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Student</TableHead><TableHead>Status</TableHead><TableHead>Notes</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((s, i) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-semibold text-sm">{s.first_name} {s.last_name}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {['present', 'absent', 'late', 'excused'].map(status => (
                              <button
                                key={status}
                                onClick={() => { const n = [...attendanceEntries]; n[i] = { ...n[i], status }; setAttendanceEntries(n); }}
                                className={`px-2 py-1 rounded text-xs font-bold capitalize transition-colors ${
                                  attendanceEntries[i]?.status === status
                                    ? status === 'present' ? 'bg-emerald-100 text-emerald-700'
                                    : status === 'absent' ? 'bg-red-100 text-red-700'
                                    : status === 'late' ? 'bg-amber-100 text-amber-700'
                                    : 'bg-blue-100 text-blue-700'
                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                                data-testid={`attendance-${s.id}-${status}`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input placeholder="Notes..." value={attendanceEntries[i]?.notes || ''} onChange={e => { const n = [...attendanceEntries]; n[i] = { ...n[i], notes: e.target.value }; setAttendanceEntries(n); }} className="h-8 text-sm" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-4 flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setAttendanceEntries(prev => prev.map(e => ({ ...e, status: 'present' })))}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                    data-testid="mark-all-present-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark All Present
                  </Button>
                  <Button onClick={submitAttendance} className="bg-[#1B2A4A]" data-testid="attendance-mark-submit">
                    <CheckCircle className="w-4 h-4 mr-1" /> Save Attendance
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {selectedClass && students.length === 0 && <p className="text-slate-400 text-center py-8">No students in this class</p>}
        </TabsContent>

        {/* Assignments */}
        <TabsContent value="assignments">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{assignments.length} assignment{assignments.length !== 1 ? 's' : ''}</p>
            <Button onClick={() => setShowAssignmentDialog(true)} className="bg-[#1B2A4A]" data-testid="create-assignment-btn">
              <PenLine className="w-4 h-4 mr-1" /> New Assignment
            </Button>
          </div>
          {assignments.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No assignments created yet</p>
          ) : (
            <div className="space-y-3">
              {assignments.map(a => (
                <Card key={a.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-[#1B2A4A]">{a.title}</p>
                      <p className="text-xs text-slate-500">{a.subject_name} | {a.class_name} | Due: {a.due_date}</p>
                      {a.instructions && <p className="text-xs text-slate-400 mt-1 line-clamp-1">{a.instructions}</p>}
                    </div>
                    <Badge variant="outline">{a.class_name}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
            <DialogContent>
              <DialogHeader><DialogTitle>New Assignment</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={assignmentForm.title} onChange={e => setAssignmentForm({...assignmentForm, title: e.target.value})} data-testid="assignment-title-input" />
                </div>
                <div>
                  <Label>Instructions</Label>
                  <Textarea value={assignmentForm.instructions} onChange={e => setAssignmentForm({...assignmentForm, instructions: e.target.value})} rows={3} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Class</Label>
                    <Select value={assignmentForm.class_id} onValueChange={v => setAssignmentForm({...assignmentForm, class_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Select value={assignmentForm.subject_id} onValueChange={v => setAssignmentForm({...assignmentForm, subject_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{(assignmentForm.class_id ? classSubjects : subjects).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Due Date</Label><Input type="date" value={assignmentForm.due_date} onChange={e => setAssignmentForm({...assignmentForm, due_date: e.target.value})} /></div>
                <div>
                  <Label>Attachments</Label>
                  <Input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={e => setAssignmentFiles(Array.from(e.target.files))} className="mt-1" data-testid="assignment-file-input" />
                  {assignmentFiles.length > 0 && <p className="text-xs text-slate-500 mt-1">{assignmentFiles.length} file(s) selected</p>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAssignmentDialog(false)}>Cancel</Button>
                <Button onClick={submitAssignment} className="bg-[#1B2A4A]" data-testid="save-assignment-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Gradebook */}
        <TabsContent value="gradebook">
          <div className="bg-white rounded-xl border p-5 mb-6">
            <h3 className="font-bold text-[#1B2A4A] text-sm mb-4">Enter Grades</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1">1. Select Class</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger data-testid="gradebook-class-select"><SelectValue placeholder="Choose class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">2. Select Subject</Label>
                <Select value={gradeForm.subject_id} onValueChange={v => setGradeForm({...gradeForm, subject_id: v})} disabled={!selectedClass}>
                  <SelectTrigger data-testid="gradebook-subject-select"><SelectValue placeholder={selectedClass ? 'Choose subject' : 'Select class first'} /></SelectTrigger>
                  <SelectContent>
                    {classSubjects.length === 0 && <SelectItem value="_none" disabled>No subjects found for this class</SelectItem>}
                    {classSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">3. Assessment Name</Label>
                <Input placeholder="e.g., Term 1 Test, Mid-year Exam" value={gradeForm.assessment_name} onChange={e => setGradeForm({...gradeForm, assessment_name: e.target.value})} data-testid="assessment-name-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1">4. Term</Label>
                <Select value={gradeForm.term} onValueChange={v => setGradeForm({...gradeForm, term: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Term 1">Term 1</SelectItem>
                    <SelectItem value="Term 2">Term 2</SelectItem>
                    <SelectItem value="Term 3">Term 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {!selectedClass && <p className="text-slate-400 text-center py-8">Select a class to get started</p>}
          {selectedClass && !gradeForm.subject_id && <p className="text-slate-400 text-center py-8">Select a subject you teach for this class</p>}
          {selectedClass && gradeForm.subject_id && !gradeForm.assessment_name && <p className="text-slate-400 text-center py-8">Enter an assessment name (e.g., "Term 1 Test")</p>}

          {selectedClass && students.length > 0 && gradeForm.subject_id && gradeForm.assessment_name && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-500">
                    Grading <strong>{students.length}</strong> students in <strong>{classSubjects.find(s => s.id === gradeForm.subject_id)?.name || 'Subject'}</strong> - {gradeForm.assessment_name}
                  </p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>#</TableHead><TableHead>Student</TableHead><TableHead>Marks</TableHead><TableHead>Out of</TableHead><TableHead>%</TableHead><TableHead>Comments</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((s, i) => {
                      const marks = gradeForm.entries[i]?.marks || 0;
                      const total = gradeForm.entries[i]?.total_marks || 100;
                      const pct = total > 0 ? Math.round(marks / total * 100) : 0;
                      return (
                        <TableRow key={s.id}>
                          <TableCell className="text-slate-400 text-sm">{i + 1}</TableCell>
                          <TableCell className="font-semibold text-sm">{s.first_name} {s.last_name}</TableCell>
                          <TableCell>
                            <Input type="number" className="w-20 h-8" value={gradeForm.entries[i]?.marks ?? 0} onChange={e => { const n = [...gradeForm.entries]; n[i] = { ...n[i], marks: parseFloat(e.target.value)||0 }; setGradeForm({...gradeForm, entries: n}); }} data-testid={`grade-marks-${s.id}`} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" className="w-20 h-8" value={gradeForm.entries[i]?.total_marks ?? 100} onChange={e => { const n = [...gradeForm.entries]; n[i] = { ...n[i], total_marks: parseFloat(e.target.value)||100 }; setGradeForm({...gradeForm, entries: n}); }} />
                          </TableCell>
                          <TableCell>
                            <Badge className={pct >= 50 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{pct}%</Badge>
                          </TableCell>
                          <TableCell>
                            <Input className="h-8 text-sm" placeholder="Optional comment" value={gradeForm.entries[i]?.comments || ''} onChange={e => { const n = [...gradeForm.entries]; n[i] = { ...n[i], comments: e.target.value }; setGradeForm({...gradeForm, entries: n}); }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="mt-4 flex justify-end">
                  <Button onClick={submitGrades} className="bg-[#1B2A4A]" data-testid="save-grades-btn">
                    <Award className="w-4 h-4 mr-1" /> Save Grades
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>


        {/* Term Comments */}
        <TabsContent value="comments">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-60"><SelectValue placeholder="Select Class" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={commentTerm} onValueChange={setCommentTerm}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Term 1">Term 1</SelectItem>
                <SelectItem value="Term 2">Term 2</SelectItem>
                <SelectItem value="Term 3">Term 3</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!selectedClass && <p className="text-slate-400 text-center py-8">Select a class to write term comments</p>}
          {selectedClass && students.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500 mb-4">Write a personalised comment for each student's report card.</p>
                <div className="space-y-4">
                  {students.map((s, i) => (
                    <div key={s.id} className="border rounded-lg p-4">
                      <p className="font-bold text-sm text-[#1B2A4A] mb-2">{s.first_name} {s.last_name}</p>
                      <Textarea
                        placeholder={`Write a term comment for ${s.first_name}...`}
                        value={termComments[i]?.comment || ''}
                        onChange={e => {
                          const updated = [...termComments];
                          updated[i] = { ...updated[i], student_id: s.id, comment: e.target.value };
                          setTermComments(updated);
                        }}
                        rows={2}
                        className="text-sm"
                        data-testid={`term-comment-${s.id}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={saveTermComments} className="bg-[#1B2A4A]" data-testid="save-comments-btn">
                    <MessageSquare className="w-4 h-4 mr-1" /> Save Comments
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {selectedClass && students.length === 0 && <p className="text-slate-400 text-center py-8">No students in this class</p>}
        </TabsContent>

        {/* Timetable */}
        <TabsContent value="timetable">
          {timetable.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No timetable entries assigned</p>
          ) : (
            DAYS.map(day => groupedTT[day]?.length > 0 && (
              <div key={day} className="mb-4">
                <h3 className="font-bold text-[#1B2A4A] mb-2 text-sm">{day}</h3>
                <div className="space-y-2">
                  {groupedTT[day].map(e => (
                    <div key={e.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div>
                        <p className="font-semibold text-sm">Period {e.period}: {e.subject_name}</p>
                        <p className="text-xs text-slate-500">{e.class_name} | {e.start_time} - {e.end_time}</p>
                      </div>
                      <Badge variant="outline">{e.room || 'TBA'}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Announcements */}
        <TabsContent value="announcements">
          {announcements.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No announcements</p>
          ) : (
            <div className="space-y-3">
              {announcements.map(a => (
                <Card key={a.id}>
                  <CardContent className="py-4">
                    <p className="font-bold text-[#1B2A4A]">{a.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleDateString()} | {a.author_name || 'Admin'}</p>
                    <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{a.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Messages */}
        <TabsContent value="messages">
          <p className="text-sm text-slate-500 mb-4">{teacherMessages.length} message{teacherMessages.length !== 1 ? 's' : ''}</p>
          {teacherMessages.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No messages. Parents can message you from their portal.</p>
          ) : (
            <div className="space-y-3">
              {teacherMessages.map(m => (
                <Card key={m.id} className={!m.is_read && m.sender_role !== 'teacher' ? 'border-blue-200 bg-blue-50/30' : ''}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={m.sender_role === 'teacher' ? 'secondary' : 'outline'} className="text-[10px]">
                            {m.sender_role === 'teacher' ? 'You' : m.sender_role}
                          </Badge>
                          <p className="font-bold text-sm text-[#1B2A4A]">{m.subject || 'No subject'}</p>
                          {!m.is_read && m.sender_role !== 'teacher' && <Badge className="bg-blue-100 text-blue-700 text-[10px]">New</Badge>}
                        </div>
                        <p className="text-xs text-slate-500">{m.sender_name} | {new Date(m.created_at).toLocaleString()}</p>
                        <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{m.content}</p>
                      </div>
                      {m.sender_role !== 'teacher' && (
                        <Button size="sm" variant="outline" onClick={() => replyToMessage(m)} className="flex-shrink-0" data-testid={`reply-msg-${m.id}`}>
                          <Send className="w-3 h-3 mr-1" /> Reply
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Reply Dialog */}
          <Dialog open={showReplyDialog} onOpenChange={setShowReplyDialog}>
            <DialogContent>
              <DialogHeader><DialogTitle>Reply to Message</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Subject</Label><Input value={replyForm.subject} onChange={e => setReplyForm({...replyForm, subject: e.target.value})} /></div>
                <div><Label>Message</Label><Textarea rows={4} value={replyForm.content} onChange={e => setReplyForm({...replyForm, content: e.target.value})} placeholder="Type your reply..." data-testid="reply-content" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReplyDialog(false)}>Cancel</Button>
                <Button onClick={sendReply} className="bg-[#1B2A4A]" data-testid="send-reply-btn"><Send className="w-3.5 h-3.5 mr-1" /> Send Reply</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
